import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Modal, ModalOverlay, ModalContent, Box, Button, Checkbox, Flex, Grid, Link, Text, Tag, UnorderedList, ListItem, useToast,
} from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";
import { useUser } from "../../contexts/UserContext";
import {
  latestConsentByPolicy, consentStateFor, needsPrompt, isImpersonating, hashDocumentText, isConsentTarget,
} from "../../lib/policyConsentRules";
import {
  LEGAL_DOCS, AGE_CONFIRM, PAID_CHANGE, canonicalLegalText, canonicalAgeConfirmText, canonicalPaidChangeText, paidChangeConsentCopy, isPaidChangeConsentWindow,
} from "../../lib/legalDocuments";
import { ageConfirmDisplay } from "../../lib/legalDisplay";
import { LEGAL_NOTICE_HEAD, buildLegalNoticeSections } from "../../lib/legalNoticeCopy";
import { setRolloutGateOpen } from "../../lib/rolloutGate";
import { isNativePlatform } from "../../lib/platformUtils";
import useTierRolloutNotice from "../../hooks/useTierRolloutNotice";
import { rolloutSegment } from "../../lib/tierRolloutNotice";
import { tt } from "../../i18n/tt";

// ============================================================================
// 약관·개인정보처리방침 v3 첫 로그인 안내 + 재동의 — 전면 페이지, 시안 C1 「문서형」(대표 선택 2026-09-09)
//   왼쪽 목차(PC) · 오른쪽 본문 ①~④ · 하단 고정 바(체크 3 + 「동의하고 시작하기」 + 탈퇴 안내)
//   모바일: 목차 숨김, 본문 세로, 하단 바에 체크가 세로로 쌓임 (시안 C 모바일)
//
// 노출: 크리에이터 회원(isConsentTarget) **또는 파트너 회원**(role 에 Partner — 대표 지시 2026-09-09 밤 「파트너에도 재동의」) 이고, 대리 접속 아님, policy 표에서 is_active·is_required 인
//       ※ 권리사 담당자에게는 약관 재동의를 받지 않는다(D1 — 계약이 우선, 계약 갱신 때 반영). VideoRightsLayout 마운트 없음.
//       D2: 멤버십 구독료 등급 기존 회원에게는 이 화면 ③ 카드와 함께 유료 전환 동의(paid_change) 체크를 **선택**으로 같이 받는다 — 건너뛰면 PaidChangeConsentGate 가 뒤에 받는다.
//       terms / privacy / age_confirm 중 현재 버전 동의가 없는 것이 하나라도 있을 때.
//       버전 상향은 docs/ops/policy/activate-legal-v3.sql (10/01) — 그 전에는 legacy 동의가 유효해 아무것도 뜨지 않는다.
// 제외 경로: 전문을 읽는 화면(/terms-of-service, /privacy-policy, /policies)과 탈퇴 화면(/withdrawal).
// 닫힘: 「동의하고 시작하기」만. 동의하지 않으려면 탈퇴 안내(약관 제2조 4항 — 거부 = 해지). ESC·바깥 클릭·Android 뒤로가기 무시.
// 기록: user_policy_consent (policy_key·version·document_hash·method=popup) 정책마다 한 행. consent_prompt_log 에 shown.
// 겹침: 떠 있는 동안 rolloutGate 를 열어 홈의 코치마크·튜토리얼이 기다리게 한다(TierRolloutInterstitial 과 같은 규칙).
// 조회 실패·표 없음 → 조용히 안 띄운다.
// ============================================================================

const KEYS = ["terms", "privacy", "age_confirm"];
const EXCLUDED_PREFIXES = ["/terms-of-service", "/privacy-policy", "/policies", "/withdrawal", "/login", "/signup"];

const isPartnerRole = (role) => String(role || "").toLowerCase().includes("partner");
const isGateTarget = (role) => isConsentTarget(role) || isPartnerRole(role);

const BAR_H = { base: "168px", md: "76px" };

export default function LegalReconsentGate() {
  const { userInfo } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const email = userInfo?.email;
  const role = userInfo?.role;
  const creator = isConsentTarget(role);

  const [pending, setPending] = useState([]);
  const [checked, setChecked] = useState({});
  const [active, setActive] = useState("rights");
  const [busy, setBusy] = useState(false);
  const [logged, setLogged] = useState(false);
  const [paidConsented, setPaidConsented] = useState(null); // null=조회 전 · true=이미 동의(또는 대상 아님) · false=받을 수 있음
  const [paidChecked, setPaidChecked] = useState(false);

  const excluded = EXCLUDED_PREFIXES.some((p) => location.pathname.startsWith(p));

  const evaluate = useCallback(async () => {
    try {
      if (!email || !isGateTarget(role) || isImpersonating() || excluded) return setPending([]);
      const { data: policies, error: pErr } = await supabase
        .from("policy")
        .select("key, title, version, is_required, is_active, effective_from, sort_order")
        .in("key", KEYS)
        .eq("is_active", true)
        .order("sort_order");
      if (pErr || !policies || policies.length === 0) return setPending([]);
      const { data: consents, error: cErr } = await supabase
        .from("user_policy_consent")
        .select("policy_key, version, agreed, method, created_at")
        .eq("user_email", email)
        .in("policy_key", KEYS);
      if (cErr) return setPending([]);
      const latest = latestConsentByPolicy(consents);
      setPending(policies.filter((p) => needsPrompt(p, consentStateFor(p, latest[p.key]))));
    } catch {
      setPending([]);
    }
  }, [email, role, excluded]);

  useEffect(() => { evaluate(); }, [evaluate]);

  const open = pending.length > 0;

  // 크리에이터의 등급·멤버십 구독료(③ 카드) — 실패하면 요금표 문장으로
  const { notice } = useTierRolloutNotice(email, { enabled: open && creator });
  const fee = useMemo(() => {
    if (!notice || rolloutSegment(notice) !== "paid" || !(Number(notice.monthlyFee) > 0)) return null;
    return { tier: notice.tier, monthlyFee: notice.monthlyFee };
  }, [notice]);
  const sections = useMemo(() => buildLegalNoticeSections({ variant: creator ? "creator" : (isPartnerRole(role) ? "partner" : "rights"), fee }), [creator, role, fee]);

  // D2 — 유료 전환 동의를 같은 화면에서(선택). 창 밖이거나 대상이 아니면 체크를 보이지 않는다.
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!open || !fee || !isPaidChangeConsentWindow()) { if (alive) setPaidConsented(true); return; }
      try {
        const { data, error } = await supabase
          .from("user_policy_consent").select("policy_key, version, agreed, method, created_at")
          .eq("user_email", email).eq("policy_key", PAID_CHANGE.key);
        if (error) { if (alive) setPaidConsented(true); return; }
        const latest = latestConsentByPolicy(data)[PAID_CHANGE.key];
        if (alive) setPaidConsented(Boolean(latest && latest.agreed && latest.version === PAID_CHANGE.version));
      } catch { if (alive) setPaidConsented(true); }
    })();
    return () => { alive = false; };
  }, [open, fee, email]);
  const paidOffer = open && fee && paidConsented === false;
  const paidCopy = useMemo(() => (fee ? paidChangeConsentCopy({ tier: fee.tier, monthlyFee: fee.monthlyFee }) : null), [fee]);

  useEffect(() => {
    setRolloutGateOpen(open);
    return () => setRolloutGateOpen(false);
  }, [open]);

  useEffect(() => {
    if (!open || !isNativePlatform()) return undefined;
    let handle;
    import("@capacitor/app").then(({ App }) => { handle = App.addListener("backButton", () => {}); }).catch(() => {});
    return () => { if (handle) handle.then ? handle.then((h) => h.remove()) : handle.remove(); };
  }, [open]);

  useEffect(() => {
    if (!open || logged || !email) return;
    setLogged(true);
    supabase.from("consent_prompt_log")
      .insert(pending.map((p) => ({ user_email: email, policy_key: p.key, version: p.version, action: "shown" })))
      .then(() => {}, () => {});
  }, [open, logged, email, pending]);

  const allChecked = useMemo(() => pending.every((p) => checked[p.key]), [pending, checked]);

  const agree = async () => {
    if (!allChecked || busy) return;
    setBusy(true);
    try {
      const rows = [];
      for (const p of pending) {
        const text = p.key === "age_confirm" ? canonicalAgeConfirmText() : canonicalLegalText(p.key);
        rows.push({ user_email: email, policy_key: p.key, version: p.version, document_hash: await hashDocumentText(text), agreed: true, method: "popup" });
      }
      if (paidOffer && paidChecked) {
        rows.push({
          user_email: email, policy_key: PAID_CHANGE.key, version: PAID_CHANGE.version,
          document_hash: await hashDocumentText(canonicalPaidChangeText({ tier: fee.tier, monthlyFee: fee.monthlyFee })), agreed: true, method: "popup",
        });
      }
      const { error } = await supabase.from("user_policy_consent").insert(rows);
      if (error) throw error;
      setPending([]);
      setChecked({});
      toast({ title: tt("a.78343bcb", "동의가 기록되었습니다. 함께해 주셔서 고맙습니다."), status: "success" });
    } catch (e) {
      toast({ title: tt("a.2fd3c57c", "동의를 기록하지 못했습니다."), description: e.message, status: "error" });
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const jump = (id) => {
    setActive(id);
    try { document.getElementById(`legal-sec-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); } catch { /* 무시 */ }
  };
  const openDoc = (path) => window.open(path, "_blank", "noopener");
  const docsPending = pending.filter((p) => LEGAL_DOCS[p.key]);
  const agePending = pending.find((p) => p.key === AGE_CONFIRM.key);

  return (
    <Modal isOpen onClose={() => {}} size="full" closeOnEsc={false} closeOnOverlayClick={false} autoFocus trapFocus motionPreset="none">
      <ModalOverlay bg="neutral.50" />
      <ModalContent bg="neutral.50" color="neutral.800" borderRadius={0} m={0} minH="100dvh" boxShadow="none" data-testid="legal-reconsent-page">
        <Box maxW="1180px" w="100%" mx="auto" px={{ base: 4, md: 7 }} pt={{ base: 6, md: 9 }} pb={BAR_H}>
          <Grid templateColumns={{ base: "1fr", lg: "220px minmax(0, 1fr)" }} gap={{ base: 5, lg: 7 }} alignItems="start">
            {/* 왼쪽 목차 — PC 만 */}
            <Box position={{ lg: "sticky" }} top="20px" display={{ base: "none", lg: "block" }}>
              <Text fontSize="11px" letterSpacing="0.06em" textTransform="uppercase" color="neutral.400" mb={2}>{LEGAL_NOTICE_HEAD.kicker}</Text>
              <Text fontWeight="700" fontSize="17px" lineHeight="1.3" mb={4}>{LEGAL_NOTICE_HEAD.title}</Text>
              <Flex direction="column" gap={0.5} fontSize="12.5px">
                {sections.map((s) => (
                  <Box key={s.id} as="button" textAlign="left" px={2.5} py={1.5} borderRadius="7px" onClick={() => jump(s.id)}
                    bg={active === s.id ? "brand.50" : "transparent"} color={active === s.id ? "brand.700" : "neutral.600"} fontWeight={active === s.id ? "700" : "400"}>
                    {s.no} {s.title}
                  </Box>
                ))}
                <Flex mt={3} px={2.5} gap={3} fontSize="11.5px" color="neutral.400" flexWrap="wrap">
                  <Link onClick={() => openDoc("/terms-of-service")}>{tt("a.c789bc2f", "전문 보기")}</Link>
                  <Link onClick={() => openDoc("/privacy-policy")}>{tt("a.5381fd49", "개인정보처리방침")}</Link>
                </Flex>
              </Flex>
              <Text mt={5} fontSize="11.5px" color="neutral.400" lineHeight="1.6">{LEGAL_NOTICE_HEAD.readTime}</Text>
            </Box>

            {/* 본문 */}
            <Flex direction="column" gap={3.5} maxW="820px" minW={0}>
              <Box display={{ base: "block", lg: "none" }} mb={1}>
                <Text fontSize="11px" letterSpacing="0.06em" textTransform="uppercase" color="neutral.400">{LEGAL_NOTICE_HEAD.kicker}</Text>
                <Text fontWeight="700" fontSize="22px" lineHeight="1.3" letterSpacing="-0.02em">{LEGAL_NOTICE_HEAD.title}</Text>
              </Box>
              <Text fontSize="14px" color="neutral.600" lineHeight="1.75">{LEGAL_NOTICE_HEAD.lead}</Text>
              {sections.map((s) => (
                <Box key={s.id} id={`legal-sec-${s.id}`} bg="white" border="1px solid" borderColor="neutral.200" borderRadius="12px" px={{ base: 4, md: 5 }} py={4} scrollMarginTop="16px">
                  <Flex align="center" gap={2} mb={2} flexWrap="wrap">
                    <Text fontWeight="700" fontSize="15px">{s.no} {s.title}</Text>
                    <Tag size="sm" bg="neutral.100" color="neutral.600" fontWeight="500">{s.chip}</Tag>
                  </Flex>
                  {s.why && (
                    <Text borderLeft="3px solid" borderColor="brand.400" pl={3} fontSize="13px" color="neutral.600" lineHeight="1.7" mb={2}>{s.why}</Text>
                  )}
                  {s.body && <Text fontSize="13.5px" color="neutral.800" fontWeight="600" mb={2}>{s.body}</Text>}
                  {s.kv && (
                    <Grid templateColumns="88px 1fr" rowGap={1} columnGap={3} fontSize="13px">
                      {s.kv.map((row) => (
                        <React.Fragment key={row.k}>
                          <Text color="neutral.500">{row.k}</Text>
                          <Text color="neutral.800" fontWeight={row.strong ? "700" : "400"}>{row.v}</Text>
                        </React.Fragment>
                      ))}
                    </Grid>
                  )}
                  {s.listTitle && <Text fontSize="13px" fontWeight="700" mt={1} mb={1}>{s.listTitle}</Text>}
                  {s.list && (
                    <UnorderedList fontSize="13px" color="neutral.700" spacing={0.5} ml={5} lineHeight="1.7">
                      {s.list.map((li) => <ListItem key={li}>{li}</ListItem>)}
                    </UnorderedList>
                  )}
                  {s.note && <Text fontSize="12.5px" color="neutral.500" mt={2}>{s.note}</Text>}
                  {s.id === "paid" && paidOffer && paidCopy && (
                    <Box mt={3} borderTop="1px dashed" borderColor="neutral.200" pt={3}>
                      <Checkbox colorScheme="brand" isChecked={paidChecked} onChange={(e) => setPaidChecked(e.target.checked)} alignItems="flex-start">
                        <Text fontSize="13px" fontWeight="600" lineHeight="1.6">{tt("a.058e66fe", "(선택) {{v0}}", { v0: paidCopy.consent })}</Text>
                      </Checkbox>
                      <Text fontSize="12px" color="neutral.500" mt={1} pl={6}>{tt("a.e8acb669", "지금 동의하지 않으셔도 됩니다 — 결제 시작 전에 다시 확인받습니다. {{v0}}", { v0: paidCopy.note })}</Text>
                    </Box>
                  )}
                </Box>
              ))}
              <Flex gap={4} fontSize="12.5px" color="neutral.500" px={1} flexWrap="wrap">
                <Link textDecoration="underline" onClick={() => openDoc("/terms-of-service")}>{tt("a.d4d4985e", "이용약관 전문")}</Link>
                <Link textDecoration="underline" onClick={() => openDoc("/privacy-policy")}>{tt("a.315a0c85", "개인정보처리방침 전문")}</Link>
                <Link textDecoration="underline" onClick={() => openDoc("/terms-of-service/2025")}>{tt("a.a243100a", "현행 약관 (2025-03-21)")}</Link>
              </Flex>
            </Flex>
          </Grid>
        </Box>

        {/* 하단 고정 바 */}
        <Box position="fixed" left={0} right={0} bottom={0} bg="white" borderTop="1px solid" borderColor="neutral.200" boxShadow="0 -10px 30px rgba(0,0,0,0.08)" px={{ base: 4, md: 7 }} py={{ base: 3, md: 3 }} zIndex={1}>
          <Flex maxW="1120px" mx="auto" gap={{ base: 2, md: 3 }} align={{ base: "stretch", md: "center" }} direction={{ base: "column", md: "row" }}>
            {docsPending.map((p) => (
              <Checkbox key={p.key} colorScheme="brand" isChecked={Boolean(checked[p.key])} onChange={(e) => setChecked((c) => ({ ...c, [p.key]: e.target.checked }))}
                border="1px solid" borderColor="neutral.200" borderRadius="9px" px={3} py={2} bg="white">
                <Text fontSize="12.5px" fontWeight="600">
                  {LEGAL_DOCS[p.key].title} {p.version}{" "}
                  <Link fontSize="11.5px" fontWeight="400" color="brand.700" textDecoration="underline" onClick={(e) => { e.preventDefault(); openDoc(LEGAL_DOCS[p.key].path); }}>{tt("a.abf64730", "달라진 점")}</Link>
                </Text>
              </Checkbox>
            ))}
            {agePending && (
              <Checkbox colorScheme="brand" isChecked={Boolean(checked[AGE_CONFIRM.key])} onChange={(e) => setChecked((c) => ({ ...c, [AGE_CONFIRM.key]: e.target.checked }))}
                border="1px solid" borderColor="neutral.200" borderRadius="9px" px={3} py={2} bg="white" title={ageConfirmDisplay().hint}>
                <Text fontSize="12.5px" fontWeight="600">{ageConfirmDisplay().label}</Text>
              </Checkbox>
            )}
            <Box flex={1} display={{ base: "none", md: "block" }} />
            <Flex align="center" gap={3} justify={{ base: "space-between", md: "flex-end" }}>
              <Text fontSize="11.5px" color="neutral.500" textAlign={{ base: "left", md: "right" }} lineHeight="1.5">
                {tt("a.6dcaf508", "동의하지 않으시면 이용이 멈추고, 미정산 수익은 그대로 지급됩니다.")}{" "}
                <Link color="neutral.500" textDecoration="underline" onClick={() => navigate("/withdrawal")}>{tt("a.2116d4e8", "자세히")}</Link>
              </Text>
              <Button colorScheme="brand" size="md" isDisabled={!allChecked} isLoading={busy} onClick={agree} px={5}>{tt("a.1499ecbc", "동의하고 시작하기")}</Button>
            </Flex>
          </Flex>
        </Box>
      </ModalContent>
    </Modal>
  );
}
